import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { StaticQuery, graphql } from 'gatsby'
import { createGlobalStyle } from 'styled-components'
import { Normalize } from 'styled-normalize'

import media from 'utils/media-queries'
import ResponsiveHelper from 'utils/responsiveHelper'
import { color, fontSize } from 'styles/theme'

const windowGlobal = typeof window !== 'undefined' && window

const hexToRgb = hex =>
  hex
    .replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (m, r, g, b) => '#' + r + r + g + g + b + b
    )
    .substring(1)
    .match(/.{2}/g)
    .map(x => parseInt(x, 16))

const GlobalStyle = createGlobalStyle`

  /* ----- Base ----- */

  html {
    height: 100%;
    -webkit-tap-highlight-color: transparent;
    font-size: 100%;
    overflow: auto;
  }

  body {
    font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif;
    height: 100%;
    color: ${color.grey900};
    font-feature-settings: "liga","kern";
    font-variant-ligatures: contextual common-ligatures;
    font-kerning: normal;
    scroll-behavior: smooth;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.4;
    font-size: ${fontSize.f4};
    -webkit-tap-highlight-color: transparent;
    ${media.md`
      font-size: ${fontSize.f3};
    `}
  }

  div {
    box-sizing: border-box;
  }

  /* ----- Typography ----- */

  b, em, strong {
    font-weight: 700;
  }

  em {
    font-style: normal;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    padding: 0;
    line-height: 1.2;
    margin: 0;
  }

  h1 {
    font-size: ${fontSize.f9};
    margin-bottom: 2rem;
    letter-spacing: -0.03125rem;
    ${media.lg`
      font-size: ${fontSize.f8};
      margin-bottom: 1.5rem;
    `}
    ${media.md`
      font-size: ${fontSize.f7};
      margin-bottom: 1.25rem;
    `}
    ${media.sm`
      margin-bottom: 1rem;
    `}
    ${media.xs`
      font-size: ${fontSize.f6};
      margin-bottom: 0.75rem;
    `}
  }

  h2 {
    letter-spacing: -0.025rem;
    font-size: ${fontSize.f6};
    margin-bottom: 1rem;
    ${media.lg`
      font-size: ${fontSize.f5};
      margin-bottom: 0.75rem;
    `}
  }

  h3 {
    letter-spacing: -0.01875rem;
    font-size: ${fontSize.f5};
    margin-bottom: 1rem;
    ${media.lg`
      font-size: ${fontSize.f4};
      margin-bottom: 0.75rem;
    `}
  }

  h4 {
    letter-spacing: -0.01875rem;
    font-size: ${fontSize.f4};
    margin-bottom: 1rem;
    ${media.lg`
      font-size: ${fontSize.f3};
      margin-bottom: 0.75rem;
    `}
  }

  h5 {
    letter-spacing: -0.0125rem;
    font-size: ${fontSize.f3};
    margin-bottom: 1rem;
    ${media.lg`
      font-size: ${fontSize.f2};
      margin-bottom: 0.75rem;
    `}
  }

  h6 {
    letter-spacing: 0rem;
    font-size: ${fontSize.f2};
    margin-bottom: 1rem;
    ${media.lg`
      font-size: ${fontSize.f1};
      margin-bottom: 0.75rem;
    `}
  }

  /* ----- Link ----- */

  a {
    color: ${color.grey900};
    &:visited {
      color: ${color.grey900};
    }
    &:active {
      color: ${props => props.themeColor};
    }
    &:hover {
      color: ${props => props.themeColor};
    }
    &:focus {
      ${props => (props.displayOutlines ? '' : 'none')}
    };
  }

  /* ----- List ----- */

    li {
    margin-bottom: 0.5rem;
    padding-left: 0.25rem;
  }

  dl, ol, ul {
    margin-bottom: 2rem;
  }

  /* ----- Selection ----- */

  ::selection {
    background: ${props =>
      props.selectionColor
        ? `rgba(${hexToRgb(props.selectionColor)[0]}, ${
            hexToRgb(props.selectionColor)[1]
          }, ${hexToRgb(props.selectionColor)[2]}, .99)`
        : 'rgba(0, 125, 255, .99)'};
    color: white;
  }

  img::selection {
    background: ${props =>
      props.selectionColor
        ? `rgba(${hexToRgb(props.selectionColor)[0]}, ${
            hexToRgb(props.selectionColor)[1]
          }, ${hexToRgb(props.selectionColor)[2]}, .7)`
        : 'rgba(0, 125, 255, .7)'};
  }

  /* ----- Code ----- */

  code {
    color: ${color.grey700};
    border: 1px solid ${color.grey300};
    background: ${color.grey100};
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    display: inline-block;
    font-size: ${fontSize.f3};
    ${media.lg`
      font-size: ${fontSize.f2};
    `}
  }

  /* ----- Quote ----- */

  blockquote {
    border-left: 3px solid ${color.grey300};
    margin-bottom: 2rem;
    padding-left: 1.25rem;
  }


  /* ----- Button ----- */

  button, input[type='button'], input[type='reset'], input[type='submit'] {
    font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif;
    font-size: 1rem;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    display: inline-block;
    line-height: 1.25rem;
    height: auto;
    border: 2px solid transparent;
    vertical-align: middle;
    -webkit-appearance: none;
    color: white;
    background-color: ${props => props.themeColor};
    padding: 1rem 3rem;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.3px;
    border-radius: ${props =>
      props.roundness === 0
        ? '0px'
        : props.roundness === 1
        ? '2px'
        : props.roundness === 2
        ? '4px'
        : '8px'};
    ${media.lg`
      padding: 1rem 2rem;
    `}
    ${media.md`
      padding: 1rem 1rem;
    `}
    :focus {
      outline: none;
      box-shadow: ${props =>
        props.displayOutlines ? '0 0 0 2px ' + color.grey400 : 'none'}
    }
    :hover {
      background-color: ${props => props.hoverColor};
    }
  }

  ::-moz-focus-inner {
    border: 0;
    padding: 0;
  }

  /* ----- Form ----- */

  input,
  textarea,
  select {
    -webkit-appearance: none;
    font-size: 1rem;
    background-color: white;
    border: none;
    border-radius: ${props =>
      props.roundness === 0
        ? '0px'
        : props.roundness === 1
        ? '2px'
        : props.roundness === 2
        ? '4px'
        : '8px'};
    box-shadow: none;
    box-sizing: border-box;
    height: 3.5rem;
    padding: 0.75rem;
    width: 100%;
    position: relative;
    box-shadow: 0 0 0 1px ${color.grey400};
    :focus {
      box-shadow: 0 0 0 2px ${props => props.themeColor};
      outline: none;
    }
  }

  input[type='checkbox'] {
    display: inline;
    width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
    /* reset input styles without bumping specificity */
    -webkit-appearance: checkbox;
    box-shadow: initial;
  }

  input[type='radio'] {
    display: inline;
    width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
    /* reset input styles without bumping specificity */
    -webkit-appearance: radio;
    box-shadow: initial;
  }

  input[type='submit'] {
    box-shadow: initial;
  }

  select {
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 8" width="30"><path fill="%231B1B1B" d="M0,0l6,8l6-8"/></svg>') center right no-repeat, white;
    :focus {
      background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 8" width="30"><path fill="%23007DFF" d="M0,0l6,8l6-8"/></svg>') center right no-repeat, white;
    }
    padding: 0.75rem 2.5rem 0.75rem 0.75rem ;
    &[multiple] {
      background: white;
      height: auto
    }
  }

  textarea {
    min-height: 5rem;
  }

  label, legend {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  label {
    cursor: pointer;
  }

  fieldset {
    border-width: 0;
    padding: 0;
  }
  
  .label-inline {
    display: inline-block;
    font-weight: normal;
    transform: translate(0, -4px);
  }

  /* ----- Table ----- */

  table {
    border-spacing: 0;
    display: table;
    overflow-x: initial;
    text-align: left;
    width: 100%;
    font-size: ${fontSize.f3};
    ${media.sm`
      width: auto;
      display: block;
      overflow-x: auto;
      max-width: calc(100vw - 32px);
    `}
    ${media.xs`
      max-width: calc(100vw - 16px);
    `}
  }

  tr {
    :last-of-type {
      td {
        border-bottom: none;
      }
    }
  }

  td, th {
    border-bottom: 1px solid ${color.grey300};
    padding: 0.75rem 1.5rem;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }
    ${media.md`
      padding: 0.5rem 1rem;
    `}
    ${media.sm`
      padding: 0.5rem 1rem;
    `}
    ${media.xs`
      padding: 0.25rem 0.5rem;
    `}
  }

  th {
    padding-bottom: 1rem;
  }

  /* ----- Divider ----- */

  hr {
    border: 0;
    border-top: 1px solid ${color.grey300};
    margin: 1.5rem 0;
  }

  /* ----- Spacing ----- */

  button, dd, dt, li {
    margin-bottom: 0.5rem;
  }

  p, fieldset, input, select, textarea {
    margin-bottom: 1rem;
  }

  dl, figure, form, ol, pre, table, ul {
    margin-bottom: 1.5rem
  }

  p {
    margin-bottom: 1rem;
  }

  p + h1 {
    margin-top: 3rem;
  }

  p + h2 {
    margin-top: 2rem;
  }

  p + h3, p + h4, p + h5, p + h6 {
    margin-top: 1.5rem;
  }

  p + ul, p + blockquote {
    margin-top: 2rem;
  }

  ${props => props.customGlobalStyle};
`

const GlobalWrapper = props => {
  const [displayOutlines, setDisplayOutlines] = useState(false)

  const handleKeyboardInput = e => {
    const key = e.keyCode || e.charCode
    // Tab
    if (key === 9) {
      setDisplayOutlines(true)
    }
  }

  useEffect(() => {
    windowGlobal.addEventListener('keydown', e => handleKeyboardInput(e))
  })

  return (
    <>
      <Normalize />
      <GlobalStyle
        displayOutlines={displayOutlines}
        customGlobalStyle={props.customGlobalStyle}
        roundness={props.roundness || 1}
        themeColor={props.themeColor || color.blue500}
        hoverColor={props.hoverColor || color.blue400}
        selectionColor={props.selectionColor}
      />
      {process.env.NODE_ENV === 'development' && <ResponsiveHelper />}
      {props.children}
    </>
  )
}

export default GlobalWrapper
